import {
  AlertTriangle,
  Bell,
  Calendar,
  CheckCircle,
  CreditCard,
  FileWarning,
  Fuel,
  IdCard,
  Info,
  RefreshCw,
  Wrench,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { extractErrorMessage } from '@/lib/api-client';
import { formatRelative } from '@/lib/format';
import {
  useAlertRules,
  useCheckAlerts,
  useMarkAllAsRead,
  useMarkAsRead,
  useNotifications,
  NOTIFICATION_TYPE_LABELS,
  type NotificationItem,
} from './api';

const TYPE_ICONS: Record<string, React.ReactNode> = {
  cnh_expiring: <IdCard className="size-5" />,
  crlv_expiring: <FileWarning className="size-5" />,
  maintenance_overdue: <AlertTriangle className="size-5" />,
  maintenance_upcoming: <Calendar className="size-5" />,
  checklist_nok: <Wrench className="size-5" />,
  fueling_anomaly: <Fuel className="size-5" />,
  system: <Info className="size-5" />,
};

const TYPE_COLORS: Record<string, string> = {
  cnh_expiring: 'bg-warning/10 text-warning-foreground',
  crlv_expiring: 'bg-warning/10 text-warning-foreground',
  maintenance_overdue: 'bg-destructive/10 text-destructive',
  maintenance_upcoming: 'bg-primary/10 text-primary',
  checklist_nok: 'bg-destructive/10 text-destructive',
  fueling_anomaly: 'bg-warning/10 text-warning-foreground',
  system: 'bg-muted text-muted-foreground',
};

export function NotificationsPage() {
  const allQuery = useNotifications({ page: 1, limit: 50 });
  const unreadQuery = useNotifications({ page: 1, limit: 50, status: 'pending' });
  const rulesQuery = useAlertRules();
  const markRead = useMarkAsRead();
  const markAllRead = useMarkAllAsRead();
  const checkAlerts = useCheckAlerts();

  const all = allQuery.data?.data ?? [];
  const unread = unreadQuery.data?.data ?? [];
  const rules = rulesQuery.data?.data ?? [];

  const handleMarkAllRead = async () => {
    try {
      await markAllRead.mutateAsync();
      toast.success('Todas as notificações marcadas como lidas');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const handleCheckAlerts = async () => {
    try {
      const result = await checkAlerts.mutateAsync();
      const total = Object.values(result).reduce((a, b) => a + (b as number), 0);
      toast.success(`Verificação concluída: ${total} alertas gerados`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <div className="page">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <Bell className="size-3.5" /> Administração
          </div>
          <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">Notificações</h1>
          <p className="text-sm text-muted-foreground">Central de alertas automáticos e avisos do sistema.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleCheckAlerts} loading={checkAlerts.isPending}>
            <RefreshCw className="size-4" /> Verificar alertas
          </Button>
          {unread.length > 0 && (
            <Button variant="outline" onClick={handleMarkAllRead}>
              <CheckCircle className="size-4" /> Marcar todas como lidas
            </Button>
          )}
        </div>
      </header>

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all"><Bell className="size-4" /> Todas ({all.length})</TabsTrigger>
          <TabsTrigger value="unread">
            <AlertTriangle className="size-4" /> Não lidas ({unread.length})
          </TabsTrigger>
          <TabsTrigger value="rules"><CreditCard className="size-4" /> Regras de alerta ({rules.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          <NotificationList
            items={all}
            loading={allQuery.isLoading}
            onMarkRead={(id) => markRead.mutateAsync(id).then(() => toast.success('Marcada como lida'))}
          />
        </TabsContent>

        <TabsContent value="unread">
          <NotificationList
            items={unread}
            loading={unreadQuery.isLoading}
            onMarkRead={(id) => markRead.mutateAsync(id).then(() => toast.success('Marcada como lida'))}
            emptyMessage="Nenhuma notificação pendente"
          />
        </TabsContent>

        <TabsContent value="rules">
          {rulesQuery.isLoading ? (
            <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : rules.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                <Bell className="size-10 text-muted-foreground" />
                <p className="text-sm font-medium">Nenhuma regra configurada</p>
                <p className="text-xs text-muted-foreground">
                  Use "Verificar alertas" para criar as regras padrão automaticamente.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {rules.map((rule) => (
                <Card key={rule.id}>
                  <CardContent className="flex items-center justify-between gap-4 p-4">
                    <div className="flex items-center gap-3">
                      <div className={`flex size-10 items-center justify-center rounded-lg ${TYPE_COLORS[rule.type] ?? 'bg-muted text-muted-foreground'}`}>
                        {TYPE_ICONS[rule.type] ?? <Bell className="size-5" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{NOTIFICATION_TYPE_LABELS[rule.type] ?? rule.type}</p>
                        <p className="text-xs text-muted-foreground">
                          {rule.daysBeforeExpiry}d antes · {rule.channels} · {rule.recipients}
                        </p>
                      </div>
                    </div>
                    {rule.enabled ? (
                      <Badge variant="success">Ativo</Badge>
                    ) : (
                      <Badge variant="outline">Inativo</Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function NotificationList({
  items,
  loading,
  onMarkRead,
  emptyMessage = 'Nenhuma notificação',
}: {
  items: NotificationItem[];
  loading: boolean;
  onMarkRead: (id: string) => void;
  emptyMessage?: string;
}) {
  if (loading) {
    return <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}</div>;
  }
  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <Bell className="size-10 text-muted-foreground" />
          <p className="text-sm font-medium">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-2">
      {items.map((n) => (
        <Card
          key={n.id}
          className={n.status !== 'read' ? 'border-l-4 border-l-primary' : 'opacity-75'}
        >
          <CardContent className="flex items-start gap-3 p-4">
            <div className={`mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-lg ${TYPE_COLORS[n.type] ?? 'bg-muted text-muted-foreground'}`}>
              {TYPE_ICONS[n.type] ?? <Bell className="size-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-medium">{n.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
                </div>
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {formatRelative(n.createdAt)}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Badge variant="secondary" className="text-[10px]">
                  {NOTIFICATION_TYPE_LABELS[n.type] ?? n.type}
                </Badge>
                {n.status !== 'read' && (
                  <button
                    onClick={() => onMarkRead(n.id)}
                    className="text-[11px] font-medium text-primary hover:underline"
                  >
                    Marcar como lida
                  </button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
